import { useEffect, useRef, useState } from 'react';
import type { FocusEvent } from 'react';
import { Calendar } from 'primereact/calendar';
import { IonIcon } from '@ionic/react';
import { format, parse } from 'date-fns';
import { useMask } from '@react-input/mask';
import { calendarOutline } from 'ionicons/icons';
import { FormEvent } from 'primereact/ts-helpers';
import { Popover } from 'react-tiny-popover';
import { getRangeForTimespan } from '../../../../../utils/filters/getRangeForTimespan';
import DateRangePickerUi from '../../../../DateRangePickerUi/DateRangePickerUi';
import Input from '../../../../Input/Input';
import Button from '../../../../Button/Button';
import {
  DATE_API_FORMAT,
  DATE_INPUT_FORMAT,
  DATE_MASK,
  DATE_MASK_OPTIONS,
  DATE_TIME_MASK,
  DATETIME_API_FORMAT,
  DATETIME_CHECKBOX_ID,
  DATETIME_INPUT_FORMAT,
  DATE_TIME_MASK_OPTIONS,
  START_OF_DAY_TIME_VALUES,
} from '../../../../../constants';
import { DateTimeInputControl } from '../../../../../types/types';
import type { GridInputBaseProps } from '../../types';
import { useTranslate } from '../../../../../hooks/context/useTranslate';
import { Checkbox } from '../../../../Checkbox';
import { getMinMaxDates } from '../../../../../utils/managerUtils';
import { getApiStringFromDateAndTime } from '../../utils/getApiStringFromDateAndTime';
import { clickedOnDatetimeCheckbox } from '../../utils/clickedOnDatetimeCheckbox';
import { datetimeMaskModify } from '../../utils/datetimeMaskModify';
import { parseInputDateTimeValue } from '../../utils/parseInputDateTimeValue';
import { normalizeDateTime } from './utils/normalizeDateTime';
import { isTargetInsidePickerElements } from '../../utils/isTargetInsidePickerElements';
import { isNamedDayPresetValue } from './utils/isNamedDayPresetValue';
import { useMinMaxDateTimeValue } from '../../hooks/useMinMaxDateTimeValue';
import InputErrorMessage from '../../../InputErrorMessage/InputErrorMessage';

type DateTimePickerProps = GridInputBaseProps & {
  control: DateTimeInputControl;
  dateOnly?: boolean;
  isFormInput?: boolean;
};

const DateTimePicker = ({
  onChange,
  onTouch,
  onFocusIn,
  onFocusOut,
  value,
  name,
  error,
  setError,
  disabled,
  isColumnFilter,
  control,
  dateOnly = false,
  isFormInput,
}: DateTimePickerProps) => {
  const { t } = useTranslate();
  // In forms time is always allowed and the toggle is hidden; never for date-only fields.
  const forceAllowTime = !!isFormInput && !dateOnly;
  const [date, setDate] = useState(new Date());
  const [time, setTime] = useState(new Date());
  const [displayedDateTime, setDisaplayedDateTime] = useState('');
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [allowTimeState, setAllowTime] = useState(false);
  const allowTime = forceAllowTime || allowTimeState;
  const popoverRef = useRef<HTMLDivElement>(null);
  // Column-filter mode prefills today's date on first mount; after a user clear we must not refill, so this flips on the initial pass.
  const initialColumnFilterFillDoneRef = useRef(false);

  // Emit value to both change and touch in the same moment — non-text controls
  // treat `touch` as an undebounced equivalent of `change`. `propagateHandler`
  // defaults to true; pass false for programmatic writes (mount defaults etc.)
  // to skip firing the field-level handlers in FormHandlersContext.
  const emitChange = (v: string, propagateHandler: boolean = true) => {
    onChange(v, propagateHandler);
    if (propagateHandler) {
      onTouch?.(v);
    }
  };

  // True when target is inside input or popover — onFocusIn/Out should not fire.
  const disableCallFocusHandler = (target: EventTarget | null): boolean =>
    isTargetInsidePickerElements(
      target,
      allowTime ? datetimeMaskRef.current : dateMaskRef.current,
      popoverRef.current
    );

  const handleFocusIn = (e: FocusEvent) => {
    if (!disableCallFocusHandler(e.target)) {
      onFocusIn?.();
    }
  };

  const handleFocusOut = (e: FocusEvent | MouseEvent) => {
    if (!disableCallFocusHandler(e.target)) {
      onFocusOut?.();
    }
  };
  const { minValue, maxValue } = useMinMaxDateTimeValue(
    control.min,
    control.max
  );

  const inputFormat = allowTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT;
  const apiFormat = allowTime ? DATETIME_API_FORMAT : DATE_API_FORMAT;

  const datetimeMaskRef = useMask({
    ...DATE_TIME_MASK_OPTIONS,
    // Prevents mask appearing after clicking the allow time checkbox
    showMask: displayedDateTime !== '',
    // Fixes mask appearing only after entering the second symbol
    modify: (trackingData) =>
      datetimeMaskModify(
        trackingData,
        DATE_TIME_MASK_OPTIONS,
        displayedDateTime
      ),
  });

  const dateMaskRef = useMask({
    ...DATE_MASK_OPTIONS,
    // Prevents mask appearing after clicking the allow time checkbox
    showMask: displayedDateTime !== '',
    // Fixes mask appearing only after entering the second symbol
    modify: (trackingData) =>
      datetimeMaskModify(trackingData, DATE_MASK_OPTIONS, displayedDateTime),
  });

  useEffect(() => {
    let newDateTime = new Date();

    let hasCorrectValue = false;
    const parsed = value ? parseInputDateTimeValue(value, dateOnly) : null;
    const carriesTime = !!parsed?.carriesTime;
    const hasTime = !dateOnly && carriesTime;
    const showTime = hasTime || forceAllowTime;
    if (value) {
      hasCorrectValue = true;
      if (hasTime) {
        setAllowTime(true);
      }
      if (!value.includes(',')) {
        if (isNamedDayPresetValue(value)) {
          const namedRange = getRangeForTimespan(value);
          if (namedRange.from) {
            newDateTime = namedRange.from;
            const dateTimeString = format(
              newDateTime,
              showTime ? DATETIME_API_FORMAT : DATE_API_FORMAT
            );
            emitChange(dateTimeString, false);
          }
        } else if (parsed) {
          newDateTime = parsed.date;
          if (!hasTime) {
            newDateTime.setHours(...START_OF_DAY_TIME_VALUES);
          }
        } else {
          // Unrecognized value (e.g. "1") — ignore: show & submit empty.
          hasCorrectValue = false;
          emitChange('', false);
          setError(null);
        }
      } else {
        // Invalid value after switching from 'between' matchmode
        hasCorrectValue = false;
        emitChange('', false);
        setError(null);
      }
    }
    const { normalizedDateTime, shouldResetAllowTime } = normalizeDateTime(
      newDateTime,
      hasCorrectValue,
      hasTime,
      minValue,
      maxValue
    );
    newDateTime = normalizedDateTime;
    if (shouldResetAllowTime) {
      // Sync checkbox state — reset when value carries no time component
      setAllowTime(false);
    }
    setDate(newDateTime);
    setTime(newDateTime);
    // Column-filter mode prefills today's date only on the very first render. After the user clears the input, we keep the display empty instead of refilling the default.
    const shouldColumnFilterPrefill =
      isColumnFilter && !initialColumnFilterFillDoneRef.current && !value;
    if (hasCorrectValue || shouldColumnFilterPrefill) {
      checkMinMaxError(newDateTime, minValue, maxValue, showTime);
      const inputString = format(
        newDateTime,
        showTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT
      );
      setDisaplayedDateTime(inputString);
      // Emit the app-format value so submit matches the display: column-filter prefill, a time-only value (today + time), and a datetime value (seconds truncated to :00).
      if (isColumnFilter || carriesTime) {
        const apiString = format(
          newDateTime,
          showTime ? DATETIME_API_FORMAT : DATE_API_FORMAT
        );
        emitChange(apiString, false);
      }
    } else {
      // Either non-filter reset (filterFirst re-init) or column filter cleared post-mount — show empty.
      setDisaplayedDateTime('');
    }
    initialColumnFilterFillDoneRef.current = true;
    //TODO: t in dependencies is a temporary fix for error messages not showing
    // It will probably not be needed after client-side routing merge
    // value dep keeps display in sync with parent updates; column-filter mode prefills today only on first mount so a user clear stays empty instead of being refilled.
    // dateOnly dep re-emits a time-carrying value truncated to the date when the user switches to a date-only match mode.
  }, [t, value, dateOnly]);

  const checkMinMaxError = (
    dateTime: Date,
    minValue: Date | null,
    maxValue: Date | null,
    allowTime: boolean
  ) => {
    const { min, max } = getMinMaxDates(minValue, maxValue, allowTime);
    const inputFormat = allowTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT;
    if (min && dateTime < min) {
      const readableMinValue = format(min, inputFormat);
      setError([t('errors.minValue').replace('{0}', readableMinValue)]);
      return true;
    } else if (max && dateTime > max) {
      const readableMaxValue = format(max, inputFormat);
      setError([t('errors.maxValue').replace('{0}', readableMaxValue)]);
      return true;
    }
    return false;
  };

  const manualDateTimeChange = (v) => {
    const mask = allowTime ? DATE_TIME_MASK : DATE_MASK;
    if (v === mask) {
      v = '';
    }
    setDisaplayedDateTime(v);

    setError(null);
    if (v === '') {
      emitChange('');
    } else {
      const newDateTime = parse(v, inputFormat, new Date());
      if (newDateTime.getDate()) {
        setDate(newDateTime);
        setTime(newDateTime);
        const dateTimeString = format(newDateTime, apiFormat);
        emitChange(dateTimeString);
      }
    }
  };

  const setInputAndApiValue = (
    date: Date,
    time: Date,
    allowTime: boolean,
    checkError = false
  ) => {
    setError(null);
    const dateTimeString = getApiStringFromDateAndTime(date, time, allowTime);
    const dateTime = new Date(dateTimeString);

    if (!allowTime) {
      dateTime.setHours(...START_OF_DAY_TIME_VALUES);
    }
    emitChange(dateTimeString);
    const inputFormat = allowTime ? DATETIME_INPUT_FORMAT : DATE_INPUT_FORMAT;
    const readableDateTimeString = format(dateTime, inputFormat);
    setDisaplayedDateTime(readableDateTimeString);
    if (checkError) {
      checkMinMaxError(dateTime, minValue, maxValue, allowTime);
    }
  };

  const handleDaySet = (day, namedRangeSet, isPresetClick) => {
    if (day) {
      setDate(day);
      const newTime = time;
      let newAllowTime = allowTime;
      if (isPresetClick && !dateOnly && !forceAllowTime) {
        setAllowTime(false);
        newAllowTime = false;
        newTime.setHours(...START_OF_DAY_TIME_VALUES);
        setTime(newTime);
      }
      setInputAndApiValue(day, newTime, newAllowTime, isColumnFilter);
    }
  };

  const onTimeChange = (e: FormEvent) => {
    const newTime = e.value as Date;
    setTime(newTime);
    setInputAndApiValue(date, newTime, allowTime);
  };

  const onSubmit = () => {
    setError(null);
    setInputAndApiValue(date, time, allowTime, true);
    setPopoverOpen(false);
  };

  const onBlur = (e: FocusEvent<HTMLInputElement>) => {
    // Do not show error when clicking on allow time checkbox
    if (clickedOnDatetimeCheckbox(e)) {
      return;
    }

    if (displayedDateTime !== '') {
      const newDate = parse(displayedDateTime, inputFormat, new Date());
      if (newDate.getDate()) {
        checkMinMaxError(newDate, minValue, maxValue, allowTime);
      } else {
        setError([
          t(allowTime ? 'errors.invalidDateTime' : 'errors.invalidDate'),
        ]);
      }
    }
  };

  const onCheckboxClick = (checkboxValue: boolean) => {
    setAllowTime(checkboxValue);
    if (value || isColumnFilter) {
      setInputAndApiValue(date, time, checkboxValue);
    } else if (!value && !isColumnFilter && displayedDateTime) {
      setDisaplayedDateTime('');
    }
  };

  const onClickOutside = (e: MouseEvent) => {
    handleFocusOut(e);
    setPopoverOpen(false);
    const apiString = getApiStringFromDateAndTime(date, time, allowTime);
    const dateTime = new Date(apiString);
    if (!allowTime) {
      dateTime.setHours(...START_OF_DAY_TIME_VALUES);
    }
    checkMinMaxError(dateTime, minValue, maxValue, allowTime);
  };

  // Enter commits the picked date/time, Escape closes without committing; both are swallowed so they don't bubble (e.g. into a parent column-filter Apply handler). Guarded on popoverOpen so the same keys do nothing while only the input is focused.
  const onPopoverKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!popoverOpen) return;

    if (event.key === 'Enter') {
      event.preventDefault();
      event.stopPropagation();
      onSubmit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      setPopoverOpen(false);
    }
  };

  const PickerElement = (
    <div className="datetime-picker">
      <DateRangePickerUi
        from={date}
        to={date}
        handleRangeSet={handleDaySet}
        allowRange={false}
        namedRange={undefined}
        defaultValue={null}
        defaultNamedRange={null}
        minValue={minValue}
        maxValue={maxValue}
        autoselectRange
        isDatasetFilter
      />
      {!dateOnly && !forceAllowTime && (
        <div className="datetime-checkbox-container">
          <Checkbox
            value={allowTime}
            onChange={onCheckboxClick}
            label={t('includingTime')}
            checkboxId={DATETIME_CHECKBOX_ID}
          />
        </div>
      )}
      {forceAllowTime && (
        <h3 className="datetime-time-title">{t('timeSectionTitle')}</h3>
      )}
      {allowTime && (
        <div className="timepicker-container">
          <Calendar value={time} onChange={onTimeChange} timeOnly inline />
        </div>
      )}
    </div>
  );

  const InputElement = (
    <Input
      name={name}
      value={displayedDateTime}
      onChange={manualDateTimeChange}
      disabled={disabled}
      placeholder={t(
        allowTime ? 'dateTimeInputPlaceholder' : 'dateInputPlaceholder'
      )}
      inputRef={allowTime ? datetimeMaskRef : dateMaskRef}
      onBlur={onBlur}
      onFocus={handleFocusIn}
      onKeyDown={onPopoverKeyDown}
      onClick={isColumnFilter ? undefined : () => setPopoverOpen(true)}
      error={error}
      hideErrorText={!isColumnFilter}
    />
  );

  return isColumnFilter ? (
    <div
      className="date-filter-column-container"
      onFocus={handleFocusIn}
      onBlur={handleFocusOut}
    >
      {PickerElement}
      {InputElement}
    </div>
  ) : (
    <>
      <Popover
        isOpen={popoverOpen}
        positions={['bottom', 'top', 'left', 'right']}
        onClickOutside={onClickOutside}
        align="start"
        content={
          <div
            className="datetime-popover"
            ref={popoverRef}
            onFocus={handleFocusIn}
            onBlur={handleFocusOut}
          >
            {PickerElement}
            <div className="datetime-picker-footer">
              <Button onClick={onSubmit} fill="clear" color="secondary">
                {t('use')}
              </Button>
            </div>
          </div>
        }
      >
        <div
          className="datetime-picker-container"
          onFocus={handleFocusIn}
          onBlur={handleFocusOut}
        >
          <Button
            fill="clear"
            color="secondary"
            className="datetime-picker-button"
            disabled={disabled}
            onClick={() => setPopoverOpen((v) => !v)}
          >
            <IonIcon icon={calendarOutline} />
          </Button>
          {InputElement}
        </div>
      </Popover>
      <InputErrorMessage error={error} className="datetime-error" />
    </>
  );
};

export default DateTimePicker;
